import { Injectable, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class IconService {
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);

  /**
   * Register all custom SVG icons for the application
   */
  registerIcons(): void {
    this.registerIcon('home', '/icons/home.svg');
    this.registerIcon('rocket', '/icons/rocket.svg');
    this.registerIcon('smiley', '/icons/smiley.svg');
    this.registerIcon('star', '/icons/star.svg');
    this.registerIcon('star_filled', '/icons/star_filled.svg');
    this.registerIcon('pie_chart', '/icons/pie_chart.svg');
    this.registerIcon('line_chart', '/icons/line_chart.svg');
    this.registerIcon('settings', '/icons/settings.svg');
    this.registerIcon('language_icon', '/icons/language_icon.svg');
    this.registerIcon('right_arrow', '/icons/right_arrow.svg');
    this.registerIcon('arrow_icon', '/icons/right_arrow.svg');
    this.registerIcon('file_upload_icon', '/icons/file_upload_icon.svg');
    this.registerIcon('file_export_icon', '/icons/file_export_icon.svg');
    this.registerIcon('search_icon', '/icons/search_icon.svg');
    this.registerIcon('filter_icon', '/icons/filter_icon.svg');
    this.registerIcon('excel_sheet_icon', '/icons/excel_sheet_icon.svg');
    this.registerIcon('csv_icon', '/icons/csv_icon.svg');
    // Admin settings icons
    this.registerIcon('tenant_icon', '/icons/tenant_icon.svg');
    this.registerIcon('role_and_permissions', '/icons/role_and_permissions.svg');
    this.registerIcon('products_icon', '/icons/products_icon.svg');
    this.registerIcon('team_members', '/icons/team_members.svg');
    this.registerIcon('store_and_location', '/icons/store_and_location.svg');
    this.registerIcon('api_users_icon', '/icons/api_users_icon.svg');
    this.registerIcon('portal_configuration_icon', '/icons/portal_configuration_icon.svg');
    this.registerIcon('client_configuration_icon', '/icons/client_configuration_icon.svg');
    this.registerIcon('site_configuration_icon', '/icons/site_configuration_icon.svg');
    this.registerIcon('widget_configuration_icon', '/icons/widget_configuration_icon.svg');
    this.registerIcon('expiration_rules_icon', '/icons/expiration_rules_icon.svg');
    // Balance-type chips on the Expiration Rules › Member Lifecycle tab.
    this.registerIcon('points_balance_icon', '/icons/points_balance_icon.svg');
    this.registerIcon('coupons_balance_icon', '/icons/coupons_balance_icon.svg');
    this.registerIcon('punches_balance_icon', '/icons/punches_balance_icon.svg');
    this.registerIcon('custom_delete_icon', '/icons/custom_delete_icon.svg');
    this.registerIcon('delete_icon', '/icons/delete_icon.svg');
    this.registerIcon('create_tenant_icon', '/icons/create_tenant_icon.svg');
    // Additional common icons that might be useful
    this.registerIcon('dashboard', '/icons/dashboard.svg');
    this.registerIcon('users', '/icons/users.svg');
    this.registerIcon('analytics', '/icons/analytics.svg');
    this.registerIcon('reports', '/icons/reports.svg');
    this.registerIcon('campaigns', '/icons/campaigns.svg');
    this.registerIcon('members', '/icons/members.svg');
    this.registerIcon('incentives', '/icons/incentives.svg');
    this.registerIcon('segmentation', '/icons/segmentation.svg');

    // team members menu icons
    this.registerIcon('profile_menu_icon', '/icons/profile_menu_icon.svg');
    this.registerIcon('role_and_permission_menu_icon', '/icons/role_and_permission_menu_icon.svg');
    this.registerIcon('tenant_menu_icon', '/icons/tenant_menu_icon.svg');
    this.registerIcon('change_role_icon', '/icons/change_role_icon.svg');
    this.registerIcon('excel_sheet_icon', '/icons/excel_sheet_icon.svg');
    this.registerIcon('csv_icon', '/icons/csv_icon.svg');

    this.registerIcon('edit_menu_icon', '/icons/edit_menu_icon.svg');
    
    // Menu icons for tenant actions
    this.registerIcon('visibility_menu', '/icons/visibility_menu.svg');
    this.registerIcon('team_member_menu', '/icons/team_member_menu.svg');
    this.registerIcon('voucher_coupon_icon', '/icons/voucher_coupon_icon.svg');
    this.registerIcon('vaadin_gift', '/icons/vaadin_gift.svg');
    this.registerIcon('delete_menu', '/icons/delete_menu.svg');
    this.registerIcon('grid_view_icon', '/icons/grid_view_icon.svg');
    this.registerIcon('table_view_icon', '/icons/table_view_icon.svg');
    this.registerIcon('lightbulb_amber_icon', '/icons/lightbulb_amber.svg');
    this.registerIcon('date_picker_icon', '/icons/date_picker_icon.svg');
    this.registerIcon('total_cards', '/icons/total_cards.svg');
    this.registerIcon('tabler_coins', '/icons/tabler_coins.svg');
    this.registerIcon('draft_icon', '/icons/draft_icon.svg');
    // Catalogs step: the tiers/layers glyph for the dashboard cards, and the
    // duplicate glyph its "Clone Catalog" action uses.
    this.registerIcon('layers', '/icons/layers.svg');
    this.registerIcon('clone_icon', '/icons/clone_icon.svg');
    this.registerIcon('archive_icon', '/icons/archive_icon.svg');
  }

  /**
   * Register a single SVG icon
   * @param name - The name to register the icon under
   * @param url - The URL path to the SVG file
   */
  private registerIcon(name: string, url: string): void {
    this.iconRegistry.addSvgIcon(
      name,
      this.sanitizer.bypassSecurityTrustResourceUrl(url)
    );
  }

  /**
   * Register an icon from a string SVG content
   * @param name - The name to register the icon under
   * @param svgContent - The SVG content as a string
   */
  registerIconFromString(name: string, svgContent: string): void {
    this.iconRegistry.addSvgIconLiteral(
      name,
      this.sanitizer.bypassSecurityTrustHtml(svgContent)
    );
  }

  /**
   * Register multiple icons from an object
   * @param icons - Object with icon names as keys and URLs as values
   */
  registerMultipleIcons(icons: Record<string, string>): void {
    Object.entries(icons).forEach(([name, url]) => {
      this.registerIcon(name, url);
    });
  }

  /**
   * Check if an icon is registered
   * @param name - The icon name to check
   * @returns Promise<boolean> - Whether the icon is registered
   */
  async isIconRegistered(name: string): Promise<boolean> {
    try {
      const svgElement = await this.iconRegistry.getNamedSvgIcon(name).toPromise();
      return !!svgElement;
    } catch {
      return false;
    }
  }
}

